import { Box, Flex, Grid, Heading, VStack, Text } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useState, useCallback } from 'react';

import useIsMobile from 'lib/hooks/useIsMobile';
import LinkInternal from 'ui/shared/links/LinkInternal';

import LatestSourceItem from './LatestSourceItem';

interface Source {
  _id: {
    $oid: string;
  };
  address: string;
  client: string;
  confirmed_task_count: number;
  createdAt: string;
  image_content: {
    cpu?: string;
    nvidia?: string;
    amd?: string;
    name: string;
    description: string;
    logo_url: string;
    tags: Array<string>;
  };
  image_metadata_url: string;
  last_updated_at: number;
  registered_provider_count: number;
  responded_task_count: number;
  source_constraints: {
    delivery_method: number;
    image_environments: number;
    max_expiry_duration: number;
    min_available_lockup: string;
    min_payment: string;
    optional_params_url: string;
    privacy_enabled: boolean;
  };
  status: string;
  total_response_time: number;
  total_task_count: number;
  updatedAt: string;
  published_at: number;
  total_offered_reward: number;
  total_payment: number;
}

interface SourceResponse {
  status: string;
  message: string;
  data: {
    sources: Array<Source>;
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
}

function LatestSource() {
  const isMobile = useIsMobile();
  const [ isLoading, setIsLoading ] = useState(true);
  const [ sources, setSources ] = useState<Array<Source>>([]);
  const maxDisplayCount = isMobile ? 2 : 3;

  // Fetch sources data function
  const fetchSourcesData = useCallback(() => {
    fetch('https://management-backend.opengpu.network/api/sources')
      .then((res) => res.json())
      .then((data: unknown) => {
        const responseData = data as SourceResponse;
        if (responseData?.status === 'success' && responseData?.data?.sources) {
          setSources(responseData.data.sources.slice(0, maxDisplayCount));
        }
        setIsLoading(false);
      })
      .catch(() => {
        setSources([]);
        setIsLoading(false);
      });
  }, [ maxDisplayCount ]);

  useEffect(() => {
    // Set loading state before initial fetch
    setIsLoading(true);

    // Initial fetch
    fetchSourcesData();

    // Set up interval to fetch data every 5 seconds
    const intervalId = setInterval(fetchSourcesData, 30_000);

    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, [ fetchSourcesData ]);

  let content;

  if (isLoading) {
    // Loading state
    content = <Text>Loading...</Text>;
  } else if (sources.length === 0) {
    // Empty state
    content = <Text>No sources found.</Text>;
  } else {
    content = (
      <VStack
        spacing={ 2 }
        mb={ 3 }
        overflow="visible"
        alignItems="stretch"
        width="100%"
      >
        <AnimatePresence initial={ false }>
          <Grid
            templateColumns={ isMobile ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)' }
            gap={ 3 }
            width="100%"
            overflow="visible"
          >
            { sources.map((source) => (
              <LatestSourceItem
                key={ source._id.$oid }
                source={ source }
                isLoading={ isLoading }
              />
            )) }
          </Grid>
        </AnimatePresence>
      </VStack>
    );
  }

  return (
    <Box width={{ base: '100%' }} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center" mb={ 3 }>
        <Heading as="h4" size="sm">
          Latest sources
        </Heading>
        <LinkInternal
          fontSize="sm"
          href="https://management.opengpu.network/sources"
          isExternal
        >
          View all sources
        </LinkInternal>
      </Flex>
      <Box mt={ 3 } width="100%">
        { content }
      </Box>
    </Box>
  );
}

export default LatestSource;
